/**
 * A journal — a page of blocks about one thing that is fermenting.
 *
 * The mead, the cider, the hot sauce and the jar of kimchi, written up as the
 * ferment goes. A batch is the structured record and this is the story beside
 * it (PLAN-JOURNAL D-J1); a journal stands alone, or names batches and blends
 * block by block, which is what makes a barrel filled from three brews and
 * blended twice one page rather than five.
 *
 * Four rules shape everything below.
 *
 * **Nothing at the core of a journal assumes alcohol.** `text`, `photo`, `file`
 * and `measurement` are the core block kinds and none of them knows what beer
 * is. A measurement is a {@link journalQuantitySchema} and a number in canonical
 * units, with a short label — "Must", "Brine", "Jar 2" — so the same block
 * records a mead's gravity and a kimchi's salt. The blocks that *do* need a
 * beer (`batch-recipe`, `batch-telemetry`, `blend-summary`) sit on top and ask
 * for it inside themselves when it is missing (D-J3, D-M1).
 *
 * **Two kinds name bytes rather than holding them.** A `photo` and a `file`
 * each carry a descriptor — the id the Worker minted, the type it sniffed, the
 * length it stored — while the object itself lives in R2 under a key built from
 * the session's user id. That is what lets a page lay itself out offline, and it
 * is why removing either kind is not undoable: the route that takes the block
 * off deletes the bytes (D-N1, D-N3).
 *
 * **Blocks are a vertical stack in array order** (D-J2). Not a grid: a phone in
 * a garage has one column, and array order is what a command log can move one
 * item of. Every block carries `at` — when the thing *happened*, which is the
 * brewer's date and not the edit's — and `public`, whether a shared page shows
 * it. Time is the spine of the record, so `at` is what a chart marks and what
 * the stack can be sorted by; the stored order stays the brewer's own, because
 * an introduction may sit at the top of a page whose first entry is a month old.
 *
 * **A calculator block stores its inputs and nothing else** (D-J5). The answer
 * is a pure function of them, recomputed from `@beerwright/core`, exactly as
 * the blend document keeps its components and lets `evaluateBlend` do the
 * arithmetic. One implementation of every formula, and a stored figure can
 * never disagree with the code that produced it.
 *
 * **A block owns its link, and reads it live** (D-M1, D-J4). A `batch-recipe`
 * or `batch-telemetry` block carries the `batchId` it draws, a `blend-summary`
 * block the `blendId`, and neither ever copies what it finds there: the batch
 * and the blend are read as they stand now, so a corrected original gravity
 * moves on the page the moment it moves on the record. Both ids are nullable,
 * because a chart on a device-pinned journal draws the journal's own series and
 * names no batch (D-J11), and because a block added before its subject is
 * chosen is a block with a picker in it rather than a refusal. The pointer was
 * on the *journal* until 2026-09-16; {@link journalDocSchema} still reads those
 * documents, and says below how.
 *
 * The calculator variants are a `z.discriminatedUnion('calculator', …)` nested
 * inside the `z.discriminatedUnion('type', …)` over block kinds. Zod 4.4.3
 * accepts that nesting at the type level and at runtime — checked before it was
 * written — so the flattened `z.union` fallback the plan allows was not needed.
 *
 * No `schemaVersion` on the document: neither blends nor water profiles carry
 * one, and the workspace stamp covers the envelope.
 */

import { z } from 'zod';
import {
  batchPhotoContentTypeSchema,
  measuredPhSchema,
  measuredTempCSchema,
  measuredVolumeSchema,
  specificGravitySchema,
} from './batch';
import { blendPrimingSugarSchema } from './blend';
import { documentIdSchema, nameSchema, percentSchema, tagsSchema, timestampSchema } from './common';

/**
 * How many blocks one journal holds.
 *
 * Two hundred, which is a daily note through a six-month mead with room for the
 * photographs, and short of a document that syncs to every device becoming a
 * blog archive. Enforced by `journal.addBlock` as well as by the `.max()` on
 * the array, because the array bound is a *parse* rule and a command that is
 * refused has to be refused before it reaches storage — the same argument
 * {@link BATCH_MAX_PHOTOS} carries.
 */
export const JOURNAL_MAX_BLOCKS = 200;

/** How long one text block may be. A page, not a chapter. */
export const JOURNAL_TEXT_MAX_LENGTH = 4000;

/** How long a measurement's label may be — "Must", "Brine", "Jar 2". */
export const JOURNAL_LABEL_MAX_LENGTH = 80;

/** How long a caption or a one-line note may be. Matches a batch photo's caption. */
export const JOURNAL_NOTE_MAX_LENGTH = 280;

/**
 * How much of a file's own name is kept, in characters.
 *
 * A hundred and twenty, which is longer than anything a lab or a maltster
 * actually calls a PDF and short enough that the longest of them still sits on
 * one line of a phone. It is a *display* bound and nothing else: the name never
 * reaches a key, a path or a header, because the object is addressed by the id
 * the Worker minted (see `apps/web/src/worker/journal-photos.ts`).
 */
export const JOURNAL_FILE_NAME_MAX_LENGTH = 120;

/**
 * What a file on a page may be, and it is a short list on purpose (D-N2).
 *
 * The things a brewer already has as files — a grain invoice, a water report, a
 * maltster's spec sheet, a spreadsheet of readings off a device we do not read
 * — plus the three image types a photograph may be, because a scan of an
 * invoice arrives as a JPEG and refusing it would be a rule nobody could
 * explain.
 *
 * **Nothing that a browser would run.** No SVG and no HTML, which are documents
 * with script in them rather than files; no archives, because what is inside one
 * is not what the sniff saw. A journal is the one document in this app a
 * stranger can open, so the list is what a page may hold and the Worker's sniff
 * is what decides whether these bytes are on it.
 *
 * Recorded on the document so a page can say what a file is without fetching
 * it. It is **not** what the serve routes trust: they sniff on the way in and
 * send the object's own stored type back with `nosniff` and an
 * `attachment` disposition, so nothing here is ever rendered inline.
 */
export const journalFileContentTypeSchema = z.enum([
  'application/pdf',
  'text/csv',
  'text/plain',
  'text/markdown',
  'application/json',
  'image/jpeg',
  'image/png',
  'image/webp',
]);

/** @see journalFileContentTypeSchema */
export type JournalFileContentType = z.infer<typeof journalFileContentTypeSchema>;

/**
 * What a measurement block measures.
 *
 * Deliberately domain-neutral quantities rather than a brewing vocabulary: a
 * hot-sauce maker records pH and percent, a mead maker gravity and
 * temperature, and a vegetable ferment a brine mass. Anything narrower would
 * put beer at the core of a document that exists precisely because not
 * everything fermenting is beer (D-J3).
 *
 * `ta` is titratable acidity in **g/L**, the seventh, and it passes that test:
 * a cider maker, a wine maker, a sour brewer and a hot-sauce maker all titrate,
 * and pH alone cannot say how much acid is in the jar. g/L as tartaric-or-lactic
 * equivalent is the unit the blending page already prints its TA in, so a
 * reading written here and a figure computed there read the same.
 */
export const journalQuantitySchema = z.enum([
  'gravity',
  'ph',
  'ta',
  'temperature',
  'mass',
  'volume',
  'percent',
]);

/** @see journalQuantitySchema */
export type JournalQuantity = z.infer<typeof journalQuantitySchema>;

/**
 * What every block carries, whatever kind it is.
 *
 * Spread into each variant rather than extended from a base object, because a
 * `z.discriminatedUnion` wants concrete `strictObject`s as its members and
 * `.extend()` on a shared schema would put the discriminant in the wrong place.
 *
 * No `.default()` on any key. Defaults survive `.partial()`, and a change-set
 * schema derived from a block would then carry `public: true` on every patch
 * that omitted it and quietly un-hide a block the brewer had hidden — the trap
 * `ingredientLinkSchema` in `common.ts` documents.
 */
const journalBlockBase = {
  /** Stable id within this journal. */
  blockId: documentIdSchema,
  /** When this happened, **ms since epoch**. The brewer's date, not the edit's. */
  at: timestampSchema,
  /** Whether a shared page shows this block (D-J6). */
  public: z.boolean(),
};

/** Prose: the note a brewer types. */
export const journalTextBlockSchema = z.strictObject({
  ...journalBlockBase,
  type: z.literal('text'),
  body: z.string().max(JOURNAL_TEXT_MAX_LENGTH),
});

/**
 * One photograph on the page.
 *
 * **The bytes are not here**, exactly as they are not on a batch photo: they
 * are one R2 object whose key the Worker builds from the *session's* user id
 * plus the journal and photo ids, so this document names an image without
 * naming a place. `contentType` and `bytes` are what a page needs to lay out
 * and to count against the account's storage total without fetching anything.
 *
 * ## The dimensions are here so the frame can be drawn before the bytes arrive
 *
 * `width` and `height` are the *stored* picture's pixels, measured off the
 * canvas that re-encoded it, and they are on the document for the same reason
 * `contentType` and `bytes` are: a page that has them can reserve the tile's
 * exact box on the first paint and not jump when the `<img>` lands (D-LD8).
 * They are document state written by the client, like the caption — the upload
 * route neither sends them nor is asked to.
 *
 * **Nullable rather than defaulted**, and `null` means "uploaded before we
 * recorded dimensions". Blocks are never `.partial()`ed, so the trap
 * `ingredientLinkSchema` documents in `common.ts` cannot fire here; the rule is
 * followed anyway, because a `.default(0)` would be a *measurement* nobody
 * took, and a tile cannot tell a real number from a made-up one. A `null` is
 * legible: the renderer falls back to a 4:3 box, which reserves some space
 * rather than the right space.
 */
export const journalPhotoBlockSchema = z.strictObject({
  ...journalBlockBase,
  type: z.literal('photo'),
  /** The R2 key's last segment. */
  photoId: documentIdSchema,
  contentType: batchPhotoContentTypeSchema,
  bytes: z
    .number()
    .int()
    .min(1)
    .max(64 * 1024 * 1024),
  /** The stored picture's width in pixels, or `null` if it was never measured. */
  width: z.number().int().min(1).max(20_000).nullable(),
  /** The stored picture's height in pixels, or `null` if it was never measured. */
  height: z.number().int().min(1).max(20_000).nullable(),
  caption: z.string().max(JOURNAL_NOTE_MAX_LENGTH),
});

/**
 * One file on the page: a report, a receipt, a spreadsheet (D-N1).
 *
 * {@link journalPhotoBlockSchema}'s construction with a different list of types
 * and a name on it. The bytes are an R2 object under `journal-files/` — a
 * sibling prefix to the photographs, so the account sweep and the one 250 MB
 * account total cover both without a second budget (D-J10) — and this block is
 * the descriptor, which is what lets a page list what it holds while offline.
 *
 * `name` is the *original* file name, carried because it is the only thing on a
 * download that means anything to a brewer: `jf-4f2a…` is what the object is
 * called and "Ward Labs 2026-03.pdf" is what the file is. It is display text
 * and nothing more — the object is addressed by `fileId`, so a name with a
 * slash or a `..` in it cannot reach a key.
 *
 * **A file is never rendered** (D-N2). Every read of one, the owner's and the
 * shared page's alike, is served as an attachment with the stored type and
 * `nosniff`, so what a page offers is a download and never a frame.
 */
export const journalFileBlockSchema = z.strictObject({
  ...journalBlockBase,
  type: z.literal('file'),
  /** The R2 key's last segment. */
  fileId: documentIdSchema,
  contentType: journalFileContentTypeSchema,
  bytes: z
    .number()
    .int()
    .min(1)
    .max(64 * 1024 * 1024),
  /** What the brewer's own machine called it. Display text; see above. */
  name: z.string().max(JOURNAL_FILE_NAME_MAX_LENGTH),
  caption: z.string().max(JOURNAL_NOTE_MAX_LENGTH),
});

/** A number somebody read off an instrument, in canonical units. */
export const journalMeasurementBlockSchema = z.strictObject({
  ...journalBlockBase,
  type: z.literal('measurement'),
  quantity: journalQuantitySchema,
  /** Canonical units: SG, pH, °C, kg, L, percent 0–100. */
  value: z.number().finite(),
  /** What was measured: "Must", "Brine", "Jar 2". Empty is allowed. */
  label: z.string().max(JOURNAL_LABEL_MAX_LENGTH),
  note: z.string().max(JOURNAL_NOTE_MAX_LENGTH),
});

/**
 * One batch's frozen recipe, rendered read-only.
 *
 * The `batchId` is the whole payload: the block renders whatever that batch
 * holds *now* (D-J4, D-M1), and copying the recipe in here would let the story
 * disagree with the record. `null` is a block whose batch has not been chosen
 * yet, which the page draws as a picker.
 *
 * `.default(null)` rather than a required key, because every block written
 * before 2026-09-16 lacks it. Defaults are the trap {@link journalBlockBase}
 * describes only for schemas that get `.partial()`ed, and a block is never one
 * of those: it is replaced whole by `journal.setBlock`.
 */
export const journalBatchRecipeBlockSchema = z.strictObject({
  ...journalBlockBase,
  type: z.literal('batch-recipe'),
  /** The batch this block is about, or `null` until one is chosen. */
  batchId: documentIdSchema.nullable().default(null),
});

/**
 * One batch's fermentation chart.
 *
 * The same pointer, for the same reason, with one difference worth saying: a
 * `null` here is not only "not chosen yet". A journal with a device pinned to
 * it draws its *own* series (D-J11), and that chart names no batch at all.
 */
export const journalBatchTelemetryBlockSchema = z.strictObject({
  ...journalBlockBase,
  type: z.literal('batch-telemetry'),
  /** The batch whose readings this draws, or `null` for the journal's own. */
  batchId: documentIdSchema.nullable().default(null),
});

/**
 * One blending session: the beers that went in, and what came out.
 *
 * Stores the pointer and nothing else, exactly as the batch blocks do, and for
 * the same reason (D-M3). Every figure on screen — the total volume, the ABV,
 * the carbonation sentence — is `evaluateBlend` over the blend as it stands
 * now, because the blend document itself stores no derived figure either.
 */
export const journalBlendSummaryBlockSchema = z.strictObject({
  ...journalBlockBase,
  type: z.literal('blend-summary'),
  /** The blending session this block is about, or `null` until one is chosen. */
  blendId: documentIdSchema.nullable().default(null),
});

/**
 * Sugars a must may be built or sweetened with. Mirrors core's `MustSugar`
 * (see that package's `must.ts`).
 */
export const journalMustSugarSchema = z.enum(['honey', 'sucrose', 'dextrose']);

/** @see journalMustSugarSchema */
export type JournalMustSugar = z.infer<typeof journalMustSugarSchema>;

/**
 * How much nitrogen a strain wants. Mirrors core's `NutrientDemand`, which is
 * TOSNA's own three bands.
 */
export const journalNutrientDemandSchema = z.enum(['low', 'medium', 'high']);

/** @see journalNutrientDemandSchema */
export type JournalNutrientDemand = z.infer<typeof journalNutrientDemandSchema>;

/**
 * The three acids a cellar keeps. Mirrors core's `CellarAcid` (`acid.ts`).
 */
export const journalCellarAcidSchema = z.enum(['tartaric', 'malic', 'citric']);

/** @see journalCellarAcidSchema */
export type JournalCellarAcid = z.infer<typeof journalCellarAcidSchema>;

/**
 * What a Pearson square's two numbers *are*.
 *
 * The arithmetic does not care — core takes them unitless — but the page does,
 * because a readout has to say "12.0 %" or "60 points" and a field has to be
 * parsed as one or the other. So the block carries the brewer's answer to that
 * question and core does not have to.
 */
export const journalPearsonMeasureSchema = z.enum(['abv', 'gravity']);

/** @see journalPearsonMeasureSchema */
export type JournalPearsonMeasure = z.infer<typeof journalPearsonMeasureSchema>;

/**
 * Degrees Brix off a refractometer.
 *
 * 0–40 covers every must a refractometer can read; the instruments themselves
 * stop at 32 °Bx and a honey must is around 26.
 */
const measuredBrixSchema = z.number().finite().min(0).max(40);

/**
 * Titratable acidity, grams per litre as tartaric.
 *
 * A wine runs 5–9 g/L and a sharp cider a little higher; 30 is a bound rather
 * than a band, there so a mistyped figure cannot reach the arithmetic.
 */
const titratableAcidSchema = z.number().finite().min(0).max(30);

/**
 * The sweetness a finished liqueur carries, grams of sugar per litre.
 *
 * An amaro runs 100–250 g/L and a rosolio higher; 1000 is a bound rather than a
 * band, well under what sucrose will dissolve to and there so a mistyped figure
 * cannot reach the arithmetic.
 */
const sugarConcentrationSchema = z.number().finite().min(0).max(1000);

/**
 * A sugar syrup's strength, °Bx.
 *
 * Higher than the refractometer's 40, because a bar syrup is 50–67 and boiling
 * one down goes higher still. 80 is roughly where sucrose stops dissolving even
 * hot, so a heavier figure is a typo rather than a syrup.
 */
const syrupBrixSchema = z.number().finite().min(0).max(80);

/**
 * Molecular SO₂ to hold, mg/L. The convention is 0.8 and a sweet wine going
 * into bottle is sometimes taken to 1.5; nothing sane is above 5.
 */
const molecularSo2Schema = z.number().finite().min(0).max(5);

/** Free SO₂ measured in the drink, mg/L. The legal ceilings are well inside 500. */
const freeSo2Schema = z.number().finite().min(0).max(500);

/** The most liquids a mixing block holds (PLAN-MIXING D-MX4). */
export const JOURNAL_MIX_MAX_LIQUIDS = 12;

/**
 * The unit a liquid on a mixing block is shown in, where the brewer picked one.
 *
 * Display only. The amount is always stored in litres or kilograms, whichever
 * `unit` says, and a `null` here shows it in the workspace's own volume or
 * mass unit.
 */
export const journalMixShownInSchema = z.enum([
  'l',
  'ml',
  'gal',
  'qt',
  'floz',
  'kg',
  'g',
  'lb',
  'oz',
]);

/** @see journalMixShownInSchema */
export type JournalMixShownIn = z.infer<typeof journalMixShownInSchema>;

/**
 * One liquid on a mixing block, mirroring core's `SpiritMixComponent`.
 *
 * The amount can be negative, which is how a distiller takes out what was run
 * off. The bound is the same size as a measured volume's, either side of zero.
 * The temperature defaults to 20 °C, which is where a measured litre needs no
 * correction, and is bounded to the −20 to 40 °C the OIML formula is fitted
 * for. It's ignored for kilograms.
 */
export const journalMixLiquidSchema = z.strictObject({
  amount: z.number().finite().min(-10_000).max(10_000).nullable().default(null),
  unit: z.enum(['L', 'kg']).default('L'),
  /** @see journalMixShownInSchema */
  shownIn: journalMixShownInSchema.nullable().default(null),
  tempC: z.number().finite().min(-20).max(40).default(20),
  abvPercent: percentSchema.nullable().default(null),
});

/** @see journalMixLiquidSchema */
export type JournalMixLiquid = z.infer<typeof journalMixLiquidSchema>;

/**
 * A calculator, kept as its inputs (D-J5).
 *
 * Eleven variants over eight of core's modules. Inputs are nullable where the
 * brewer has not typed a number yet, and the block simply shows no answer until
 * they have. The exceptions are the values an instrument or a convention already
 * decides, which the screen defaults rather than asking for: a hydrometer's
 * calibration temperature, a carbonation target, the sugar and the acid a
 * picker starts on, the 0.8 mg/L molecular SO₂ convention, the free SO₂ a
 * brewer has not measured, and a refractometer's wort correction factor.
 *
 * ## The cellar's seven are a mead, a cider and a wine (PLAN-JOURNAL §J)
 *
 * They are here rather than on a page of their own because the journal is where
 * a mead lives: a ferment with no recipe has no batch, so a calculator that
 * belongs to it belongs to the page it is written up on. Every one of them is a
 * pure core function with a citation, and the block holds only the question.
 *
 * `refractometer` is the one variant with a mode rather than a formula: a
 * non-null `finalBrix` means the sample has alcohol in it, which is a different
 * model (`refractometerFinalGravityRaw`) and not a different calculator.
 *
 * ## The eleventh is a drink nobody fermented (PLAN-AMARO)
 *
 * `proof-and-sweeten` is an amaro's arithmetic: a jar of high-proof macerate
 * brought down to a strength and up to a sweetness in one sum, because each
 * half moves the other's answer. It sits beside the cellar's seven on the same
 * argument — sulfite, sorbate and acid adjustment are winemaking rather than
 * brewing, and a proofing tool is the same kind of neighbour.
 *
 * ## The twelfth mixes strengths (PLAN-MIXING)
 *
 * `mixing` takes a list of liquids, each an amount, a temperature and a
 * strength, and gives the strength and volume of the mix with the contraction
 * counted. It's the only calculator whose inputs are a list.
 */
export const journalCalculatorBlockSchema = z.discriminatedUnion('calculator', [
  z.strictObject({
    ...journalBlockBase,
    type: z.literal('calculator'),
    calculator: z.literal('abv'),
    inputs: z.strictObject({
      og: specificGravitySchema.nullable(),
      fg: specificGravitySchema.nullable(),
    }),
  }),
  z.strictObject({
    ...journalBlockBase,
    type: z.literal('calculator'),
    calculator: z.literal('hydrometer-correction'),
    inputs: z.strictObject({
      measuredGravity: specificGravitySchema.nullable(),
      sampleTempC: measuredTempCSchema.nullable(),
      /** The hydrometer's calibration temperature, °C. Defaults to 20 on screen. */
      calibrationTempC: measuredTempCSchema,
    }),
  }),
  z.strictObject({
    ...journalBlockBase,
    type: z.literal('calculator'),
    calculator: z.literal('priming-sugar'),
    inputs: z.strictObject({
      beerVolumeL: measuredVolumeSchema.nullable(),
      targetVolumes: z.number().finite().min(0).max(6),
      fermentationTempC: measuredTempCSchema.nullable(),
      sugar: blendPrimingSugarSchema,
    }),
  }),
  z.strictObject({
    ...journalBlockBase,
    type: z.literal('calculator'),
    calculator: z.literal('sugar-to-gravity'),
    inputs: z.strictObject({
      currentSg: specificGravitySchema.nullable(),
      targetSg: specificGravitySchema.nullable(),
      volumeL: measuredVolumeSchema.nullable(),
      /** Which sugar. Defaults to honey on screen, because this is a mead's block. */
      sugar: journalMustSugarSchema,
    }),
  }),
  z.strictObject({
    ...journalBlockBase,
    type: z.literal('calculator'),
    calculator: z.literal('nutrients'),
    inputs: z.strictObject({
      volumeL: measuredVolumeSchema.nullable(),
      originalSg: specificGravitySchema.nullable(),
      /** How hungry the strain is. Defaults to medium on screen. */
      demand: journalNutrientDemandSchema,
    }),
  }),
  z.strictObject({
    ...journalBlockBase,
    type: z.literal('calculator'),
    calculator: z.literal('sulfite'),
    inputs: z.strictObject({
      volumeL: measuredVolumeSchema.nullable(),
      ph: measuredPhSchema.nullable(),
      /** Molecular SO₂ to hold, mg/L. Defaults to the 0.8 convention on screen. */
      targetMolecularSo2MgL: molecularSo2Schema,
      /** Free SO₂ already measured, mg/L. Zero on screen until it is measured. */
      currentFreeSo2MgL: freeSo2Schema,
    }),
  }),
  z.strictObject({
    ...journalBlockBase,
    type: z.literal('calculator'),
    calculator: z.literal('sorbate'),
    inputs: z.strictObject({
      volumeL: measuredVolumeSchema.nullable(),
      abvPercent: percentSchema.nullable(),
    }),
  }),
  z.strictObject({
    ...journalBlockBase,
    type: z.literal('calculator'),
    calculator: z.literal('acid-adjustment'),
    inputs: z.strictObject({
      volumeL: measuredVolumeSchema.nullable(),
      currentTaGL: titratableAcidSchema.nullable(),
      targetTaGL: titratableAcidSchema.nullable(),
      /** Which acid is being added. Defaults to tartaric on screen. */
      acid: journalCellarAcidSchema,
    }),
  }),
  z.strictObject({
    ...journalBlockBase,
    type: z.literal('calculator'),
    calculator: z.literal('pearson-square'),
    inputs: z.strictObject({
      /** The two components' values, in whatever {@link measure} says. */
      a: z.number().finite().nullable(),
      b: z.number().finite().nullable(),
      target: z.number().finite().nullable(),
      totalVolumeL: measuredVolumeSchema.nullable(),
      /** What the three numbers are. @see journalPearsonMeasureSchema */
      measure: journalPearsonMeasureSchema,
    }),
  }),
  z.strictObject({
    ...journalBlockBase,
    type: z.literal('calculator'),
    calculator: z.literal('refractometer'),
    inputs: z.strictObject({
      brix: measuredBrixSchema.nullable(),
      /** A second reading, taken once fermentation started. Non-null is final-gravity mode. */
      finalBrix: measuredBrixSchema.nullable(),
      /** The instrument's wort correction factor. Defaults to 1.04 on screen. */
      wortCorrectionFactor: z.number().finite().min(0.9).max(1.2),
    }),
  }),
  z.strictObject({
    ...journalBlockBase,
    type: z.literal('calculator'),
    calculator: z.literal('proof-and-sweeten'),
    inputs: z.strictObject({
      /** What is in the jar, litres. */
      volumeL: measuredVolumeSchema.nullable().default(null),
      /** How strong it is now, % ABV. */
      abvPercent: percentSchema.nullable().default(null),
      /** How strong it should end up, % ABV. */
      targetAbvPercent: percentSchema.nullable().default(null),
      /** How sweet it should end up, grams per litre of finished drink. */
      targetSugarGL: sugarConcentrationSchema.nullable().default(null),
      /**
       * What the sugar arrives as, mirroring core's `Sweetener`.
       *
       * The one calculator whose inputs are not four flat numbers, because the
       * choice carries a number of its own: a syrup has a strength and dry
       * sugar does not, and a `syrupBrix` sitting beside a `sweetener` would be
       * a field that means nothing half the time. The brix is nullable for the
       * usual reason — a syrup the maker has not measured yet — and the block
       * shows no answer until it has been.
       */
      sweetener: z
        .discriminatedUnion('kind', [
          z.strictObject({ kind: z.literal('sugar') }),
          z.strictObject({
            kind: z.literal('syrup'),
            brix: syrupBrixSchema.nullable().default(null),
          }),
        ])
        .default({ kind: 'sugar' }),
    }),
  }),
  z.strictObject({
    ...journalBlockBase,
    type: z.literal('calculator'),
    calculator: z.literal('mixing'),
    inputs: z.strictObject({
      /** The liquids going in. @see journalMixLiquidSchema */
      liquids: z.array(journalMixLiquidSchema).min(1).max(JOURNAL_MIX_MAX_LIQUIDS),
    }),
  }),
]);

/** One block on the page. @see journalDocSchema */
export const journalBlockSchema = z.discriminatedUnion('type', [
  journalTextBlockSchema,
  journalPhotoBlockSchema,
  journalFileBlockSchema,
  journalMeasurementBlockSchema,
  journalBatchRecipeBlockSchema,
  journalBatchTelemetryBlockSchema,
  journalBlendSummaryBlockSchema,
  journalCalculatorBlockSchema,
]);

/** @see journalBlockSchema */
export type JournalBlock = z.infer<typeof journalBlockSchema>;

/** @see journalBlockSchema */
export type JournalBlockType = JournalBlock['type'];

/**
 * Fields of a journal, without the one-time `batchId` → blocks lift.
 *
 * Split from {@link journalDocSchema} the way `blendDocFieldsSchema` is split
 * from `blendDocSchema`, and for the same two reasons: a payload schema needs
 * something it can `.pick()` from, and the migration below has to run *before*
 * a `strictObject` refuses the key it exists to remove.
 */
export const journalDocFieldsSchema = z.strictObject({
  id: documentIdSchema,
  name: nameSchema,
  /**
   * The brewer's own labels, one vocabulary with a recipe's (D-M4). Defaulted,
   * because every journal written before 2026-09-16 has none.
   */
  tags: tagsSchema.default([]),
  blocks: z.array(journalBlockSchema).max(JOURNAL_MAX_BLOCKS),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});

/**
 * Move a journal-level `batchId` onto the blocks that were drawing it.
 *
 * The first journal build made a journal the story of one batch and put the
 * pointer on the document (D-J13). A page is not shaped like that — a barrel is
 * filled by several brews over a year, and a beer brewed three times is one
 * story — so the link moved onto the block on 2026-09-16 (D-M1). What that
 * leaves behind is documents saved in the day between, and they say exactly
 * what the new shape says: every batch block on them was drawing the journal's
 * batch. So the key is stamped onto each batch block that names none and then
 * dropped, and nothing is lost.
 *
 * A block that already carries an id keeps it, which is what makes this
 * idempotent: a document that has been through here once has no `batchId` left
 * to stamp, and a document written by the current code is untouched.
 */
function migrateJournalDoc(raw: unknown): unknown {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return raw;
  const rec = { ...(raw as Record<string, unknown>) };
  const legacy = rec.batchId;
  delete rec.batchId;
  if (typeof legacy !== 'string' || !Array.isArray(rec.blocks)) return rec;
  rec.blocks = (rec.blocks as unknown[]).map((item) => {
    if (typeof item !== 'object' || item === null || Array.isArray(item)) return item;
    const block = item as Record<string, unknown>;
    if (block.type !== 'batch-recipe' && block.type !== 'batch-telemetry') return block;
    return typeof block.batchId === 'string' ? block : { ...block, batchId: legacy };
  });
  return rec;
}

/** A persisted journal. */
export const journalDocSchema = z.preprocess(migrateJournalDoc, journalDocFieldsSchema);

/** @see journalDocSchema */
export type JournalDoc = z.infer<typeof journalDocFieldsSchema>;

/**
 * A blank journal: named, and otherwise as empty as the caller leaves it.
 *
 * Pure — `id`, `name`, the tags, the blocks and both timestamps come from the
 * caller. The `blocks` seed is how "Start a journal" on a batch or a blend page
 * opens a page that already names what it is about (D-M2), rather than an empty
 * page with a pointer on it.
 */
export function blankJournalDoc(args: {
  readonly id: string;
  readonly name: string;
  readonly timestamp: number;
  readonly blocks?: readonly JournalBlock[];
  readonly tags?: readonly string[];
}): JournalDoc {
  return {
    id: args.id,
    name: args.name,
    tags: args.tags === undefined ? [] : [...args.tags],
    blocks: args.blocks === undefined ? [] : [...args.blocks],
    createdAt: args.timestamp,
    updatedAt: args.timestamp,
  };
}
